import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://envanterapi.360restoran.com',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to attach JWT token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken');
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Return successful response as-is
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized errors (token expired or invalid)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Attempt to refresh the token
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          // No refresh token available, redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Call refresh token endpoint
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL || 'https://envanter.360restoran.com'}/auth/refresh`,
          { refreshToken }
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Store new tokens
        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        // Update the authorization header with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        // Retry the original request with new token
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh token failed, clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other errors with Turkish error messages
    const errorMessage = getErrorMessage(error);
    
    // Attach Turkish error message to the error object
    return Promise.reject({
      ...error,
      message: errorMessage,
      originalError: error,
    });
  }
);

/**
 * Get user-friendly Turkish error message based on error type
 */
function getErrorMessage(error: AxiosError): string {
  // Network errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
    }
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  // HTTP status code based errors
  const status = error.response.status;
  const data = error.response.data as any;

  // Use backend error message if available and in Turkish
  if (data?.message) {
    if (Array.isArray(data.message)) {
      return data.message.join(', ');
    }
    return data.message;
  }

  // Default Turkish messages for common HTTP status codes
  switch (status) {
    case 400:
      return 'Geçersiz istek. Lütfen girdiğiniz bilgileri kontrol edin.';
    case 401:
      return 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.';
    case 403:
      return 'Bu işlem için yetkiniz bulunmamaktadır.';
    case 404:
      return 'İstenen kaynak bulunamadı.';
    case 409:
      return 'Bu kayıt zaten mevcut.';
    case 422:
      return 'Girilen veriler işlenemedi. Lütfen kontrol edin.';
    case 500:
      return 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.';
    case 502:
      return 'Sunucu geçici olarak kullanılamıyor.';
    case 503:
      return 'Servis şu anda bakımda. Lütfen daha sonra tekrar deneyin.';
    default:
      return `Bir hata oluştu (Kod: ${status}). Lütfen tekrar deneyin.`;
  }
}

export default apiClient;
